import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AdminProvider } from './contexts/AdminContext';
import { AdminThemeProvider } from './contexts/AdminThemeContext';
import { StorageProvider } from './contexts/StorageContext';
import AdminDashboardLayout from './components/AdminDashboardLayout';
import AdminRouteWrapper from './components/AdminRouteWrapper';
import AdminLogin from './components/AdminLogin';
import AdminIndex from './components/AdminIndex';
import StandaloneDNSChecker from './components/StandaloneDNSChecker';
import AdminTemplateManager from './components/admin/AdminTemplateManager';
import AdminTemplateBuilder from './components/admin/AdminTemplateBuilder';
import AutomationEditor from './components/AutomationEditor';
import UserContacts from './components/UserContacts';
import UserDetailsPage from './components/UserDetailsPage';
import AdminTemplateDetailsPage from './components/AdminTemplateDetailsPage';
import AdminCreateEmailTemplatePage from './components/AdminCreateEmailTemplatePage';
import ClarityRouteTracker from './components/ClarityRouteTracker';
import NotFound from './components/NotFound';

// Redirect component for legacy URLs
const LegacyRedirect: React.FC<{ page?: string }> = ({ page }) => {
  if (page) {
    return <Navigate to={`/admin/control-panel-7x9k2m/${page}`} replace />;
  }
  return <Navigate to="/admin/control-panel-7x9k2m/overview" replace />;
};

const LegacyRouteRedirect: React.FC = () => {
  const { page } = useParams<{ page?: string }>();
  return <LegacyRedirect page={page} />;
};

// Admin App - Only admin routes
function AdminApp() {
  // Debug logging
  console.log('AdminApp rendering', {
    pathname: window.location.pathname,
    hostname: window.location.hostname
  });

  return (
    <AuthProvider>
      <NotificationProvider>
        <AdminProvider>
          <AdminThemeProvider>
            <StorageProvider>
              <Router>
              <ClarityRouteTracker />
              <Routes>
                {/* Admin Login/Signup */}
                <Route path="/" element={<AdminIndex />} />
                <Route path="/login" element={<AdminLogin />} />

                {/* Admin Dashboard - Redirect root to overview */}
                <Route path="/admin/control-panel-7x9k2m" element={
                  <Navigate to="/admin/control-panel-7x9k2m/overview" replace />
                } />

                {/* Admin Dashboard - Create email templates route (needs to be before generic :page route) */}
                <Route path="/admin/control-panel-7x9k2m/create-email-templates" element={
                  <AdminRouteWrapper>
                    <AdminCreateEmailTemplatePage />
                  </AdminRouteWrapper>
                } />
                <Route path="/admin/control-panel-7x9k2m/create-email-templates/:templateId" element={
                  <AdminRouteWrapper>
                    <AdminCreateEmailTemplatePage />
                  </AdminRouteWrapper>
                } />

                {/* Admin Dashboard - Template details route (needs to be before generic :page route) */}
                <Route path="/admin/control-panel-7x9k2m/email-templates/:templateId/details" element={
                  <AdminRouteWrapper>
                    <AdminTemplateDetailsPage />
                  </AdminRouteWrapper>
                } />

                {/* Admin Dashboard - Template editor route */}
                <Route path="/admin/control-panel-7x9k2m/email-templates/:templateId/edit" element={
                  <AdminRouteWrapper>
                    <AdminTemplateBuilder />
                  </AdminRouteWrapper>
                } />

                {/* Admin Dashboard - All pages with URL slugs */}
                <Route path="/admin/control-panel-7x9k2m/:page" element={
                  <AdminRouteWrapper>
                    <AdminDashboardLayout />
                  </AdminRouteWrapper>
                } />

                {/* Legacy redirect from old URL */}
                <Route path="/control-panel-7x9k2m" element={<LegacyRedirect />} />
                <Route path="/control-panel-7x9k2m/:page" element={<LegacyRouteRedirect />} />

                {/* User Management */}
                <Route path="/user-contacts/:userId" element={
                  <AdminRouteWrapper>
                    <UserContacts />
                  </AdminRouteWrapper>
                } />

                <Route path="/admin/control-panel-7x9k2m/user-details/:userId" element={
                  <AdminRouteWrapper>
                    <UserDetailsPage />
                  </AdminRouteWrapper>
                } />

                <Route path="/user-details/:userId" element={
                  <AdminRouteWrapper>
                    <UserDetailsPage />
                  </AdminRouteWrapper>
                } />

                {/* Template Management */}
                <Route path="/templates" element={
                  <AdminRouteWrapper>
                    <AdminTemplateManager />
                  </AdminRouteWrapper>
                } />
                <Route path="/templates/new" element={
                  <AdminRouteWrapper>
                    <AdminTemplateBuilder />
                  </AdminRouteWrapper>
                } />
                <Route path="/templates/:templateId/edit" element={
                  <AdminRouteWrapper>
                    <AdminTemplateBuilder />
                  </AdminRouteWrapper>
                } />

                {/* DNS Tools */}
                <Route path="/dns-checker" element={
                  <AdminRouteWrapper>
                    <StandaloneDNSChecker />
                  </AdminRouteWrapper>
                } />

                {/* Automation Templates */}
                <Route path="/automation-template-editor" element={
                  <AdminRouteWrapper>
                    <AutomationEditor />
                  </AdminRouteWrapper>
                } />

                {/* 404 Not Found */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Router>
            </StorageProvider>
          </AdminThemeProvider>
        </AdminProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default AdminApp;

